import React, { useState } from "react";
import { FileText, X, Clock, Save, Calendar } from "lucide-react";
import { useEvolucao } from "../context/EvolucaoContext";
import { theme } from "../../../shared/styles/theme";
import { ehDataFutura, msgDataFutura, hojeISO } from "../../../shared/utils/dataNaoFutura";
import { usePendencias } from "../../pendencias/context/PendenciasContext";
import {
  TIPO_CONSULTA_LABELS,
  TIPOS_CONSULTA_CAMPO_UNICO,
  TipoConsulta,
} from "../../../types";

interface NovaEvolucaoProps {
  pacienteId: string;
  pacienteNome: string;
  onClose: () => void;
  onSucesso?: () => void;
}

export default function NovaEvolucao({
  pacienteId,
  pacienteNome,
  onClose,
  onSucesso,
}: NovaEvolucaoProps) {
  const { adicionarEvolucao } = useEvolucao();

  const agora = new Date();
  const dataAtual = `${agora.getFullYear()}-${String(
    agora.getMonth() + 1
  ).padStart(2, "0")}-${String(agora.getDate()).padStart(2, "0")}`;
  const horaAtual = `${String(agora.getHours()).padStart(2, "0")}:${String(
    agora.getMinutes()
  ).padStart(2, "0")}`;

  const [formData, setFormData] = useState({
    data: dataAtual,
    hora: horaAtual,
    tipoConsulta: "",
    evolucaoLivre: "",
    peso: "",
    altura: "",
    fc: "",
    pa: "",
    queixaPrincipal: "",
    historiaDoencaAtual: "",
    revisaoSistemas: "",
    exameFisico: "",
    hipotesesDiagnosticas: "",
    conduta: "",
    observacoes: "",
  });

  const [salvando, setSalvando] = useState(false);
  const { adicionarPendencias } = usePendencias();
  const [pendenciasTexto, setPendenciasTexto] = useState("");

  // B90: tipos "focados" usam campo único; demais usam o form estruturado
  const campoUnico = TIPOS_CONSULTA_CAMPO_UNICO.includes(
    formData.tipoConsulta as TipoConsulta
  );

  // Atualizar campo
  const handleChange = (campo: string, valor: string) => {
    setFormData((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  // Salvar evolução
  const handleSalvar = async () => {
    if (!formData.tipoConsulta) {
      alert("❌ Selecione o Tipo de Consulta");
      return;
    }
    if (campoUnico) {
      if (!formData.evolucaoLivre.trim()) {
        alert("❌ Preencha o campo Evolução");
        return;
      }
    } else if (!formData.queixaPrincipal.trim()) {
      alert("❌ Preencha pelo menos a Queixa Principal");
      return;
    }
    // 📅 B296 — data da consulta não pode ser futura (permite hoje/retroativo).
    if (ehDataFutura(formData.data)) {
      alert(msgDataFutura("Data da consulta"));
      return;
    }
    setSalvando(true);
    try {
      const { data, hora, ...dadosFormulario } = formData;
      await adicionarEvolucao(pacienteId, {
        ...dadosFormulario,
        tipoConsulta: dadosFormulario.tipoConsulta as TipoConsulta,
        peso: dadosFormulario.peso
          ? parseFloat(dadosFormulario.peso)
          : undefined,
        altura: dadosFormulario.altura
          ? parseFloat(dadosFormulario.altura)
          : undefined,
        fc: dadosFormulario.fc ? parseFloat(dadosFormulario.fc) : undefined,
        dataManual: `${data}T${hora}:00`,
        pacienteId: pacienteId,
      });

      // Salvar pendências se houver
      const linhasPendencias = pendenciasTexto
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => l.length > 0);
      if (linhasPendencias.length > 0) {
        await adicionarPendencias(pacienteId, pacienteNome, linhasPendencias);
      }

      alert("✅ Evolução registrada com sucesso!");
      if (onSucesso) onSucesso();
      onClose();
    } catch (error) {
      alert("❌ Erro ao salvar evolução");
      console.error(error);
    } finally {
      setSalvando(false);
    }
  };

  // Estilos
  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "12px",
    border: `2px solid ${theme.gray[300]}`,
    borderRadius: theme.borderRadius.md,
    fontSize: "14px",
    fontFamily: theme.fonts.sans,
    boxSizing: "border-box",
    resize: "vertical",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "14px",
    fontWeight: "600",
    color: theme.gray[700],
    marginBottom: "6px",
    fontFamily: theme.fonts.sans,
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: "24px",
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        zIndex: 9999,
        overflow: "auto",
        padding: "20px",
      }}
    >
      <div
        style={{
          maxWidth: "1000px",
          margin: "0 auto",
          background: "white",
          borderRadius: theme.borderRadius.xl,
          padding: "32px",
          boxShadow: theme.shadows.xl,
        }}
      >
        {/* HEADER */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
            paddingBottom: "16px",
            borderBottom: `2px solid ${theme.gray[200]}`,
          }}
        >
          <div>
            <h2
              style={{
                fontSize: "24px",
                fontWeight: "bold",
                color: theme.gray[800],
                margin: "0 0 8px 0",
                fontFamily: theme.fonts.sans,
              }}
            >
              <FileText size={16} style={{ marginRight: "8px" }} /> Nova
              Consulta
            </h2>
            <p
              style={{
                fontSize: "16px",
                fontWeight: "700",
                color: theme.primary[700],
                margin: 0,
                fontFamily: theme.fonts.sans,
              }}
            >
              {pacienteNome}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: theme.gray[200],
              color: theme.gray[700],
              border: "none",
              padding: "10px 20px",
              borderRadius: theme.borderRadius.md,
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              fontFamily: theme.fonts.sans,
            }}
          >
            ✕ Fechar
          </button>
        </div>
        {/* FORMULÁRIO */}
        <div
          style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: "8px" }}
        >
          {/* DATA E HORA */}
          <div style={{ display: "flex", gap: "16px", marginBottom: "24px" }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>
                <Calendar size={14} style={{ marginRight: "6px" }} /> Data da
                Consulta
              </label>
              <input
                type="date"
                value={formData.data}
                onChange={(e) => handleChange("data", e.target.value)}
                min="1900-01-01"
                max={hojeISO()}
                style={{ ...inputStyle, resize: "none" }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>
                <Clock size={14} style={{ marginRight: "6px" }} /> Hora da
                Consulta
              </label>
              <input
                type="time"
                value={formData.hora}
                onChange={(e) => handleChange("hora", e.target.value)}
                style={{ ...inputStyle, resize: "none" }}
              />
            </div>
          </div>
          {/* SINAIS VITAIS E ANTROPOMETRIA */}
          <div style={{ display: "flex", gap: "12px", marginBottom: "24px" }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Peso (kg)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={formData.peso}
                onChange={(e) => handleChange("peso", e.target.value)}
                style={{ ...inputStyle, resize: "none" }}
                placeholder="Ex: 70.5"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Altura (cm)</label>
              <input
                type="number"
                min="0"
                value={formData.altura}
                onChange={(e) => handleChange("altura", e.target.value)}
                style={{ ...inputStyle, resize: "none" }}
                placeholder="Ex: 170"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>FC (bpm)</label>
              <input
                type="number"
                min="0"
                value={formData.fc}
                onChange={(e) => handleChange("fc", e.target.value)}
                style={{ ...inputStyle, resize: "none" }}
                placeholder="Ex: 72"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>PA (mmHg)</label>
              <input
                type="text"
                value={formData.pa}
                onChange={(e) => handleChange("pa", e.target.value)}
                style={{ ...inputStyle, resize: "none" }}
                placeholder="Ex: 120/80"
              />
            </div>
          </div>
          {/* TIPO DE CONSULTA (B90) */}
          <div style={sectionStyle}>
            <label style={labelStyle}>
              Tipo de Consulta <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <select
              value={formData.tipoConsulta}
              onChange={(e) => handleChange("tipoConsulta", e.target.value)}
              style={inputStyle}
            >
              <option value="">Selecione...</option>
              {(Object.keys(TIPO_CONSULTA_LABELS) as TipoConsulta[]).map((t) => (
                <option key={t} value={t}>
                  {TIPO_CONSULTA_LABELS[t]}
                </option>
              ))}
            </select>
          </div>

          {/* EVOLUÇÃO — CAMPO ÚNICO (retorno/pós-Tx/intercorrência) */}
          {campoUnico && (
            <div style={sectionStyle}>
              <label style={labelStyle}>
                Evolução <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <textarea
                value={formData.evolucaoLivre}
                onChange={(e) => handleChange("evolucaoLivre", e.target.value)}
                style={{ ...inputStyle, minHeight: "220px" }}
                placeholder="Descreva a evolução: queixas, achados do exame, conduta e orientações..."
              />
            </div>
          )}

          {/* CAMPOS ESTRUTURADOS (primeira consulta / pré-transplante) */}
          {!campoUnico && (
            <>
          {/* QUEIXA PRINCIPAL */}
          <div style={sectionStyle}>
            <label style={labelStyle}>
              Queixa Principal <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <textarea
              value={formData.queixaPrincipal}
              onChange={(e) => handleChange("queixaPrincipal", e.target.value)}
              style={{ ...inputStyle, minHeight: "80px" }}
              placeholder="Descreva o motivo principal da consulta..."
            />
          </div>

          {/* HISTÓRIA DA DOENÇA ATUAL */}
          <div style={sectionStyle}>
            <label style={labelStyle}>História da Doença Atual (HDA)</label>
            <textarea
              value={formData.historiaDoencaAtual}
              onChange={(e) =>
                handleChange("historiaDoencaAtual", e.target.value)
              }
              style={{ ...inputStyle, minHeight: "120px" }}
              placeholder="Descreva a evolução dos sintomas, início, duração, fatores de melhora/piora..."
            />
          </div>

          {/* REVISÃO DE SISTEMAS */}
          <div style={sectionStyle}>
            <label style={labelStyle}>Revisão de Sistemas</label>
            <textarea
              value={formData.revisaoSistemas}
              onChange={(e) => handleChange("revisaoSistemas", e.target.value)}
              style={{ ...inputStyle, minHeight: "100px" }}
              placeholder="Cardiovascular, Respiratório, Gastrointestinal, Genitourinário, Neurológico, etc..."
            />
          </div>

          {/* EXAME FÍSICO */}
          <div style={sectionStyle}>
            <label style={labelStyle}>Exame Físico</label>
            <textarea
              value={formData.exameFisico}
              onChange={(e) => handleChange("exameFisico", e.target.value)}
              style={{ ...inputStyle, minHeight: "120px" }}
              placeholder="Sinais vitais, estado geral, exame por sistemas..."
            />
          </div>

          {/* HIPÓTESES DIAGNÓSTICAS */}
          <div style={sectionStyle}>
            <label style={labelStyle}>Hipóteses Diagnósticas</label>
            <textarea
              value={formData.hipotesesDiagnosticas}
              onChange={(e) =>
                handleChange("hipotesesDiagnosticas", e.target.value)
              }
              style={{ ...inputStyle, minHeight: "100px" }}
              placeholder="Liste as principais hipóteses diagnósticas em ordem de probabilidade..."
            />
          </div>

          {/* CONDUTA / PLANO TERAPÊUTICO */}
          <div style={sectionStyle}>
            <label style={labelStyle}>Conduta / Plano Terapêutico</label>
            <textarea
              value={formData.conduta}
              onChange={(e) => handleChange("conduta", e.target.value)}
              style={{ ...inputStyle, minHeight: "120px" }}
              placeholder="Exames solicitados, medicações prescritas, orientações, retorno..."
            />
          </div>

          {/* OBSERVAÇÕES */}
          <div style={sectionStyle}>
            <label style={labelStyle}>Observações</label>
            <textarea
              value={formData.observacoes}
              onChange={(e) => handleChange("observacoes", e.target.value)}
              style={{ ...inputStyle, minHeight: "80px" }}
              placeholder="Observações adicionais, intercorrências, etc..."
            />
          </div>
            </>
          )}
          {/* PENDÊNCIAS */}
          <div
            style={{
              marginBottom: "24px",
              padding: "16px",
              background: "#fffbeb",
              border: "1px solid #f59e0b",
              borderLeft: "4px solid #f59e0b",
              borderRadius: theme.borderRadius.lg,
            }}
          >
            <label style={{ ...labelStyle, color: "#92400e" }}>
              <Clock size={14} style={{ marginRight: "6px" }} /> Pendências
              desta consulta
            </label>
            <p
              style={{
                fontSize: "12px",
                color: "#92400e",
                margin: "0 0 8px 0",
                fontFamily: theme.fonts.sans,
              }}
            >
              Uma pendência por linha. Ex: "Solicitar TC abdome", "Colher novo
              MELD"
            </p>
            <textarea
              value={pendenciasTexto}
              onChange={(e) => setPendenciasTexto(e.target.value)}
              style={{
                ...inputStyle,
                minHeight: "80px",
                background: "white",
                borderColor: "#f59e0b",
              }}
              placeholder={
                "Solicitar TC abdome\nColher novo MELD\nAgendar retorno em 30 dias"
              }
            />
          </div>
        </div>
        {/* BOTÕES DE AÇÃO */}

        {/* BOTÕES DE AÇÃO */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
            marginTop: "24px",
            paddingTop: "16px",
            borderTop: `2px solid ${theme.gray[200]}`,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "12px 24px",
              background: theme.gray[200],
              color: theme.gray[700],
              border: "none",
              borderRadius: theme.borderRadius.lg,
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              fontFamily: theme.fonts.sans,
              transition: theme.transitions.normal,
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = theme.gray[300];
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = theme.gray[200];
            }}
          >
            <X size={14} style={{ marginRight: "6px" }} /> Cancelar
          </button>

          <button
            onClick={handleSalvar}
            disabled={salvando}
            style={{
              padding: "12px 24px",
              background: salvando ? theme.gray[400] : theme.primary[600],
              color: "white",
              border: "none",
              borderRadius: theme.borderRadius.lg,
              fontSize: "14px",
              fontWeight: "600",
              cursor: salvando ? "not-allowed" : "pointer",
              fontFamily: theme.fonts.sans,
              transition: theme.transitions.normal,
            }}
            onMouseOver={(e) => {
              if (!salvando) {
                e.currentTarget.style.background = theme.primary[700];
              }
            }}
            onMouseOut={(e) => {
              if (!salvando) {
                e.currentTarget.style.background = theme.primary[600];
              }
            }}
          >
            {salvando ? (
              <>
                <Clock size={14} style={{ marginRight: "6px" }} /> Salvando...
              </>
            ) : (
              <>
                <Save size={14} style={{ marginRight: "6px" }} /> Salvar
                Consulta
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
